import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnal } from '../../../hooks'
import type { EGameType } from '../../../types/enums'
import { ECurrency, ETableLimit } from '../../../types/enums'
import { Button, ToggleSwitch } from '../../atoms'
import { GameTypeTabs } from '../../molecules'
import {
  filter,
  filterRow,
  limitButtonActive,
  limitButtonActiveBase,
  limitButtonBase,
  limitButtons,
  switchStyle,
} from './index.css'

export type LobbyFilterType = {
  tab: typeof ECurrency.COIN | typeof ECurrency.TON | typeof ECurrency.USDT
  gameType: EGameType
  limits: ETableLimit[]
  sitNow: boolean
}

export const LobbyFilter = (props: {
  options: LobbyFilterType
  onUpdate: (filter: LobbyFilterType) => void
}) => {
  const { t } = useTranslation()
  const { logEvent } = useAnal()
  const [limitFilters, setLimitFilters] = useState<ETableLimit[]>([])

  useEffect(() => {
    const filters =
      props.options.tab === ECurrency.COIN
        ? [ETableLimit.MID, ETableLimit.HIGH]
        : [
            ETableLimit.MICRO,
            ETableLimit.LOW,
            ETableLimit.MID,
            ETableLimit.HIGH,
          ]
    setLimitFilters(filters)
  }, [props.options.tab])

  const onFilterUpdate = <GField extends keyof LobbyFilterType>(
    field: GField,
    value: LobbyFilterType[GField],
  ) => {
    const isArray = Array.isArray(value)
    if (field !== 'gameType') {
      logEvent('ui_button_click', {
        name: 'lobby_filter',
        field: field.toLowerCase(),
        value: String(isArray ? value.join('_') : value).toLowerCase(),
      })
    }
    props.onUpdate({
      ...props.options,
      [field]: isArray
        ? [
            ...value.reduce((acc, newValue) => {
              if (acc.has(newValue)) {
                acc.delete(newValue)
              } else {
                acc.add(newValue)
              }
              return acc
            }, new Set(props.options.limits)),
          ]
        : value,
    })
  }

  return (
    <div className={filter}>
      <GameTypeTabs
        gameType={props.options.gameType}
        onChange={(type) => onFilterUpdate('gameType', type)}
      />
      <div className={filterRow}>
        <div className={switchStyle}>
          <span>{t('lobby.filter.sitNow')}</span>
          <ToggleSwitch
            checked={props.options.sitNow || false}
            onChange={(e) => onFilterUpdate('sitNow', e.target.checked)}
          />
        </div>
        <div className={limitButtons}>
          {limitFilters.map((limit) => (
            <Button
              key={limit}
              className={clsx(
                limitButtonBase,
                (props.options.limits || []).includes(limit) && [
                  limitButtonActiveBase,
                  limitButtonActive[limit],
                ],
              )}
              analytic={{
                name: `limit_${limit.toLowerCase()}`,
              }}
              onClick={() => onFilterUpdate('limits', [limit])}
            >
              {t(`lobby.limit.${limit}`)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
